import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { CircleNotch, DownloadSimple, Star, Target, TrendUp } from '@phosphor-icons/react';
import * as XLSX from 'xlsx';
import { GROUP_STYLE, GroupLegend, ConsensusRefLink } from '../ConsensusBadge';
import ChampionComboBanner from './ChampionComboBanner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/**
 * Per-Window Ball Predictions tab — extracted from SummaryDashboard.js.
 * Receives pre-fetched data via props; pure-render (no fetching of its own).
 *
 * Props:
 *   pwData          — per-window-ball-predictions API response (or null)
 *   pwLoading       — boolean while pwData is fetching
 *   w3Projection    — Window 3 multi-window/ensemble.w3_predictions block (or null)
 *   dateSignal      — date-pattern-signal API response (or null)
 *   exportPwToExcel — () => void (download .xlsx button handler)
 *   consensus       — useConsensusGroups() hook result { groups, groupOf, scoreOf }
 *   ballLabels      — { ball_1: 'Ball 1', ... ball_5: 'Ball 5' }
 *   lsLabels        — { lucky_star_1: 'Star 1', lucky_star_2: 'Star 2' }
 */
export default function SummaryTabPerWindow({
  pwData,
  pwLoading,
  w3Projection,
  dateSignal,
  exportPwToExcel,
  consensus,
  ballLabels,
  lsLabels,
}) {
  const navigate = useNavigate();

  return (
    <>
    {pwLoading ? (
      <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
    ) : pwData ? (
      <div className="space-y-8">
        {/* ⭐ Champion-Combo Prediction Banner — Empirical findings */}
        <ChampionComboBanner />

        {/* Consensus legend + ref link */}
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-lg px-4 py-2.5 flex items-center justify-between flex-wrap gap-3" data-testid="pw-consensus-legend">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-bold text-slate-700">🚦 Each predicted ball is color-ringed by its Consensus group:</span>
            <GroupLegend groups={consensus.groups} />
          </div>
          <ConsensusRefLink />
        </div>

        {/* Export Button */}
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={exportPwToExcel} className="gap-1.5 text-xs" data-testid="export-pw-xlsx">
            <DownloadSimple size={14} weight="bold" />
            Export Window Predictions (Excel)
          </Button>
        </div>

        {/* Window 3 Pred (avg) — Single-best guess prediction */}
        {w3Projection?.pred_avg && w3Projection.pred_avg.length > 0 && (
          <div className="bg-gradient-to-r from-violet-700 to-fuchsia-700 rounded-lg px-5 py-4 text-white" data-testid="w3-pred-avg-banner">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
              <div>
                <h3 className="font-bold text-lg">Window 3 Projection — Pred (avg)</h3>
                <p className="text-[11px] text-white/80">Single best guess per ball from Window 3&apos;s conditional-bucket analysis (Scenario #1). Bucket = <span className="font-mono font-bold">{w3Projection.bucket_range}</span></p>
              </div>
              <span className="text-[10px] bg-white/20 px-2 py-1 rounded font-bold">Auto-updated on new draw</span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {w3Projection.pred_avg.map((p) => (
                <div key={p.ball} className="bg-white/15 backdrop-blur rounded-lg p-2 text-center border border-white/20">
                  <div className="text-[10px] text-white/70 font-bold uppercase">{p.ball}</div>
                  <div className="text-3xl font-mono font-bold">{p.predicted}</div>
                  <div className="text-[10px] text-white/70 font-mono mt-1">last:{p.last} | Δ{p.avg_diff > 0 ? '+' : ''}{p.avg_diff}</div>
                  <div className="text-[9px] text-white/60 mt-1">dom {p.dominant_band} ({p.dominant_band_pct}%)</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* W15 Predicted Balance Banner */}
        {pwData.w15_balance && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg px-5 py-3 flex items-center justify-between" data-testid="w15-balance-banner">
            <div className="flex items-center gap-3 text-white">
              <span className="font-bold text-lg">W15 Predicted Balance</span>
              <span className="text-sm opacity-80">Combos matching these patterns get boosted in W2 and W22</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="bg-white/20 rounded px-3 py-1.5 text-white font-mono font-bold text-lg">{pwData.w15_balance.oe_pattern}</span>
              <span className="bg-white/20 rounded px-3 py-1.5 text-white font-mono font-bold text-lg">{pwData.w15_balance.hl_pattern}</span>
            </div>
          </div>
        )}
        {/* W20 Spread Constraint Banner */}
        {pwData.w20_spread && (
          <div className="bg-gradient-to-r from-teal-500 to-cyan-600 rounded-lg px-5 py-3" data-testid="w20-spread-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                <span className="font-bold text-lg">W20 Spread Distribution</span>
                <span className="text-sm opacity-80">Peak 30-44 = +2.5 | Extended 20-49 = +1.0 | Quartile = +1.5</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="bg-white/25 rounded px-3 py-1.5 text-white font-mono font-bold text-sm">Peak {pwData.w20_spread.peak_spread_min}-{pwData.w20_spread.peak_spread_max} ({pwData.w20_spread.peak_spread_pct}%)</span>
                <span className="bg-white/15 rounded px-3 py-1.5 text-white font-mono font-bold text-sm">Ext {pwData.w20_spread.extended_spread_min}-{pwData.w20_spread.extended_spread_max} ({pwData.w20_spread.extended_spread_pct}%)</span>
              </div>
            </div>
            {/* Spread bucket prediction table */}
            {pwData.w20_spread.bucket_stats && (
              <div className="mt-3 grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
                {[...pwData.w20_spread.bucket_stats].sort((a, b) => a.bucket - b.bucket).map(b => (
                  <div key={b.bucket} className={`rounded-lg p-2 text-center ${b.is_peak ? 'bg-white/20 ring-1 ring-white/40' : b.is_extended ? 'bg-white/10' : 'bg-white/5 opacity-60'}`} data-testid={`w20-bucket-${b.bucket}`}>
                    <div className="font-mono font-bold text-white text-sm">{b.label}</div>
                    <div className="text-white font-bold text-lg">{b.pct}%</div>
                    <div className="text-[10px] text-white/70">gap {b.current_gap}</div>
                    <div className="text-[10px] text-white/70">avg {b.avg_gap} | max {b.max_gap}</div>
                    <div className={`text-[10px] font-bold mt-0.5 ${b.gap_ratio >= 1.5 ? 'text-red-300' : b.gap_ratio >= 1 ? 'text-amber-300' : 'text-white/50'}`}>{b.gap_ratio}x due</div>
                    <div className={`font-mono font-bold text-sm mt-0.5 ${b.pred_score >= 20 ? 'text-yellow-300' : 'text-white/80'}`}>{b.pred_score}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* W20 LS Spread Constraint Banner */}
        {pwData.w20_ls_spread && (() => {
          const entries = Object.entries(pwData.w20_ls_spread).sort((a, b) => b[1].gap_ratio - a[1].gap_ratio);
          const top3 = entries.slice(0, 3);
          return (
            <div className="bg-gradient-to-r from-amber-600 to-orange-700 rounded-lg px-5 py-3" data-testid="w20-ls-spread-banner">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3 text-white">
                  <span className="font-bold text-lg">W20 LS Spread Due</span>
                  <span className="text-sm opacity-80">Overdue LS spread boosts LS pair scoring (avg gap / max gap / due ratio)</span>
                </div>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-11 gap-2">
                {entries.map(([k, v]) => (
                  <div key={k} className={`rounded-lg p-2 text-center ${v.gap_ratio >= 1.5 ? 'bg-white/25 ring-1 ring-white/40' : v.gap_ratio >= 1 ? 'bg-white/15' : 'bg-white/5'}`} data-testid={`w20-ls-${k}`}>
                    <div className="font-mono font-bold text-white text-lg">{k}</div>
                    <div className="text-white font-bold text-sm">{v.pct}%</div>
                    <div className="text-[10px] text-white/70">avg {v.avg_gap}</div>
                    <div className="text-[10px] text-white/70">max {v.max_gap}</div>
                    <div className={`text-[10px] font-bold mt-0.5 ${v.gap_ratio >= 1.5 ? 'text-red-300' : v.gap_ratio >= 1 ? 'text-amber-300' : 'text-white/50'}`}>{v.gap_ratio}x</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {/* W42 Sum & Spread Filter Banner */}
        {pwData.w42_sum_spread && (
          <div className="bg-gradient-to-r from-purple-700 to-indigo-800 rounded-lg px-5 py-3" data-testid="w42-sum-spread-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                <Star size={20} weight="fill" className="text-purple-300" />
                <span className="font-bold text-lg">W42 LS Sum & Spread Filter</span>
                <span className="text-sm opacity-80">LS pairs outside these ranges are statistically less likely</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <div className="bg-white/15 rounded-lg p-3 text-center">
                <div className="text-[10px] text-white/60 uppercase tracking-wider font-bold">Top Sums (LS1+LS2)</div>
                <div className="flex gap-1.5 justify-center mt-1.5">
                  {pwData.w42_sum_spread.top_sums?.map(s => (
                    <span key={s} className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-purple-400/40 text-white font-mono font-bold text-sm">{s}</span>
                  ))}
                </div>
              </div>
              <div className="bg-white/15 rounded-lg p-3 text-center">
                <div className="text-[10px] text-white/60 uppercase tracking-wider font-bold">Top Spreads (LS2-LS1)</div>
                <div className="flex gap-1.5 justify-center mt-1.5">
                  {pwData.w42_sum_spread.top_spreads?.map(s => (
                    <span key={s} className="inline-flex w-8 h-8 items-center justify-center rounded-full bg-indigo-400/40 text-white font-mono font-bold text-sm">{s}</span>
                  ))}
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <div className="text-[10px] text-white/60 uppercase tracking-wider font-bold">Avg Sum</div>
                <div className="text-2xl font-bold text-white font-mono mt-1">{pwData.w42_sum_spread.avg_sum}</div>
                <div className="text-[10px] text-white/50">Last: {pwData.w42_sum_spread.last_sum}</div>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <div className="text-[10px] text-white/60 uppercase tracking-wider font-bold">Avg Spread</div>
                <div className="text-2xl font-bold text-white font-mono mt-1">{pwData.w42_sum_spread.avg_spread}</div>
                <div className="text-[10px] text-white/50">Last: {pwData.w42_sum_spread.last_spread}</div>
              </div>
            </div>
          </div>
        )}
        {/* Date Pattern Signal Banner */}
        {dateSignal?.date_signals?.filter(s => s.strength === 'VERY HIGH' || s.strength === 'HIGH').length > 0 && (
          <div className="bg-gradient-to-r from-rose-600 to-red-700 rounded-lg px-5 py-3" data-testid="date-pattern-banner">
            <div className="flex items-center gap-3 text-white mb-2">
              <span className="font-bold text-lg">Date Pattern Signal</span>
              <span className="text-sm opacity-80">Recurring numbers on upcoming draw dates</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {dateSignal.date_signals.filter(s => s.strength === 'VERY HIGH' || s.strength === 'HIGH').map((sig) => (
                <div key={`ds-${sig.target_date?.date_str || sig.target_date?.date || sig.strength}`} className="bg-white/15 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-black ${sig.strength === 'VERY HIGH' ? 'bg-red-300 text-red-900' : 'bg-amber-300 text-amber-900'}`}>{sig.strength}</span>
                    <span className="text-white font-bold text-sm">{sig.target_date.date_str} ({sig.target_date.weekday})</span>
                    <span className="text-white/50 text-[10px]">{sig.exact_match_draws} historical draws</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {sig.hot_numbers.filter(h => h.date_pct >= 30).map(h => (
                      <div key={h.number} className="flex items-center gap-1">
                        <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-sm ${h.date_pct >= 50 ? 'bg-white text-red-700' : 'bg-white/30 text-white'}`}>{h.number}</span>
                        <span className="text-white/80 text-[10px] font-bold">{h.date_pct}%</span>
                      </div>
                    ))}
                    {sig.recurring_pairs?.length > 0 && (
                      <span className="text-[10px] font-bold text-white bg-white/20 px-1.5 py-0.5 rounded ml-1">
                        Pair [{sig.recurring_pairs[0].pair.join(',')}] {sig.recurring_pairs[0].pct}%
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Main Balls */}
        {Object.entries(ballLabels).map(([bk, label]) => {
          const windows = pwData.balls?.[bk] || [];
          // Count frequency of each number across all windows' top-5
          const freqMap = {};
          const scoreMap = {};
          windows.forEach(w => {
            w.predictions.forEach(p => {
              freqMap[p.number] = (freqMap[p.number] || 0) + 1;
              scoreMap[p.number] = (scoreMap[p.number] || 0) + p.score;
            });
          });
          const ranked = Object.entries(freqMap)
            .map(([num, count]) => ({ number: parseInt(num), count, totalScore: scoreMap[parseInt(num)] || 0 }))
            .sort((a, b) => b.count - a.count || b.totalScore - a.totalScore);
          return (
            <div key={bk} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`pw-${bk}`}>
              <div className="bg-[#002FA7] px-5 py-3 flex items-center gap-3">
                <span className="inline-flex w-9 h-9 rounded-full bg-white items-center justify-center font-mono font-bold text-[#002FA7] text-sm">{label.split(' ')[1]}</span>
                <h3 className="text-white font-bold text-lg">{label}</h3>
                <span className="text-white/50 text-xs ml-auto">{windows.length} windows</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold w-14">S.No.</th>
                      <th className="px-4 py-2 text-left text-[10px] uppercase text-gray-600 font-bold w-48">Window</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">#1</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">#2</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">#3</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">#4</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">#5</th>
                    </tr>
                  </thead>
                  <tbody>
                    {windows.map((w, idx) => {
                      const isCombined = w.is_combined;
                      const isStuck = w.same_as_previous === true;
                      return (
                      <tr key={`${w.window}-${w.name}-${idx}`} className={isCombined ? 'bg-gradient-to-r from-amber-50 to-emerald-50 border-2 border-amber-400' : isStuck ? 'bg-slate-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`pw-${bk}-w${w.window}-${idx}`}>
                        <td className={`px-3 py-2 border-b text-center font-mono font-bold text-gray-500 ${isCombined ? 'border-amber-300' : ''}`} data-testid={`pw-${bk}-sno-${idx}`}>{idx + 1}</td>
                        <td className={`px-4 py-2 border-b ${isCombined ? 'border-amber-300' : ''}`}>
                          <button onClick={() => navigate(w.path)} className="flex items-center gap-2 hover:text-[#002FA7] transition-colors group">
                            <span className={`inline-flex w-7 h-7 rounded items-center justify-center font-mono font-bold text-[10px] transition-colors ${isCombined ? 'bg-gradient-to-r from-amber-500 to-emerald-500 text-white' : 'bg-[#002FA7]/10 text-[#002FA7] group-hover:bg-[#002FA7] group-hover:text-white'}`}>{isCombined ? '★' : `W${w.window}`}</span>
                            <div className="flex flex-col items-start">
                              <span className={`font-semibold group-hover:text-[#002FA7] ${isCombined ? 'text-amber-800 font-bold' : 'text-gray-800'}`}>{w.name}</span>
                              {isStuck && (
                                <span className="text-[9px] text-slate-500 italic font-medium mt-0.5" data-testid={`pw-${bk}-w${w.window}-${idx}-same-as-prev`} title="The same 5 numbers were also projected for the previous draw">
                                  (Same as previous projection)
                                </span>
                              )}
                            </div>
                          </button>
                        </td>
                        {w.predictions.map((p, pi) => {
                          const grp = consensus.groupOf(p.number);
                          const gs = GROUP_STYLE[grp];
                          return (
                          <td key={pi} className={`px-3 py-2 border-b text-center ${isCombined ? 'border-amber-300' : ''}`}>
                            <div className={`inline-flex flex-col items-center ${pi === 0 ? 'font-bold' : ''}`}>
                              <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-bold text-sm ring-2 ring-offset-1 ${gs.ring} ${isCombined ? (pi === 0 ? 'bg-gradient-to-br from-amber-400 to-emerald-500 text-white shadow-lg' : 'bg-amber-100 text-amber-800 font-bold') : pi === 0 ? 'bg-[#002FA7] text-white' : pi < 3 ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-gray-100 text-gray-700'}`} title={`${grp} · Consensus ${consensus.scoreOf(p.number) >= 0 ? '+' : ''}${consensus.scoreOf(p.number)}`}>
                                {p.number}
                              </span>
                              <span className="text-[9px] text-gray-400 mt-0.5">{p.score}</span>
                            </div>
                          </td>
                          );
                        })}
                        {/* Fill empty slots if less than 5 predictions */}
                        {Array.from({length: Math.max(0, 5 - w.predictions.length)}).map((_, i) => (
                          <td key={`empty-${i}`} className="px-3 py-2 border-b text-center text-gray-300">-</td>
                        ))}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Average row for each column */}
              {windows.length > 0 && (() => {
                const avgs = [0,1,2,3,4].map(col => {
                  const nums = windows.map(w => w.predictions[col]?.number).filter(n => n != null);
                  return nums.length > 0 ? (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1) : null;
                });
                return (
                  <div className="border-t border-gray-200 bg-slate-50 px-5 py-2" data-testid={`pw-${bk}-average`}>
                    <table className="w-full text-xs">
                      <tbody>
                        <tr>
                          <td className="px-3 py-1.5 w-14 text-center text-[10px] font-bold text-gray-400 uppercase">—</td>
                          <td className="px-4 py-1.5 w-48 text-[10px] font-bold text-gray-500 uppercase">Avg</td>
                          {avgs.map((avg, i) => (
                            <td key={i} className="px-3 py-1.5 text-center">
                              {avg != null ? (
                                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#002FA7] text-white font-mono font-bold text-sm">{avg}</span>
                              ) : <span className="text-gray-300">-</span>}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              {/* Number frequency across all windows */}
              {windows.length > 0 && (() => {
                const numFreq = {};
                const numWindows = {};
                windows.forEach(w => {
                  w.predictions.forEach(p => {
                    numFreq[p.number] = (numFreq[p.number] || 0) + 1;
                    if (!numWindows[p.number]) numWindows[p.number] = [];
                    numWindows[p.number].push(`W${w.window}`);
                  });
                });
                const sorted = Object.entries(numFreq)
                  .map(([num, count]) => ({ number: parseInt(num), count, windows: numWindows[parseInt(num)] }))
                  .sort((a, b) => b.count - a.count || a.number - b.number);
                return (
                  <div className="border-t border-gray-200 bg-blue-50/50 px-5 py-3" data-testid={`pw-${bk}-freq`}>
                    <div className="text-[10px] font-bold text-gray-500 uppercase mb-2">Prediction Frequency — Which number predicted how many times</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                      {sorted.map(r => (
                        <div key={r.number} className="flex items-center gap-1.5" data-testid={`pw-${bk}-freq-${r.number}`}>
                          <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-xs ${r.count >= 5 ? 'bg-[#002FA7] text-white' : r.count >= 3 ? 'bg-[#002FA7]/20 text-[#002FA7] font-bold' : 'bg-gray-200 text-gray-700'}`}>
                            {r.number}
                          </span>
                          <span className={`text-xs font-mono ${r.count >= 5 ? 'text-[#002FA7] font-bold' : r.count >= 3 ? 'text-blue-700 font-semibold' : 'text-gray-500'}`}>
                            {r.count}x
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* Hot/Cold: 3 factors */}
              {pwData.hot_cold?.[bk] && (() => {
                const hc = pwData.hot_cold[bk];
                const factors = [
                  { key: 'position', ...hc.position },
                  { key: 'all5', ...hc.all5 },
                  { key: 'all7', ...hc.all7 },
                ];
                // Pool-scope hint per factor — makes it explicit which value range
                // the H/C numbers on each row are drawn from (e.g. "ALL 7 BALLS"
                // spans 1-50 because main balls dominate the combined pool).
                const poolHint = (k) => {
                  if (k === 'position') return `Pool: ${hc.range_75 || '1-50'} at this position`;
                  if (k === 'all5')     return `Pool: ${hc.range_75 || '1-50'} across B1-B5`;
                  if (k === 'all7')     return 'Pool: 1-50 across all 7 positions';
                  return '';
                };
                return (
                  <div className="border-t border-gray-200 bg-white px-5 py-2" data-testid={`pw-${bk}-hotcold`}>
                    {/* Legend / how-to-read note */}
                    <div className="bg-slate-50 border border-slate-200 rounded px-3 py-1.5 mb-2 text-[10px] text-slate-600 leading-snug" data-testid={`pw-${bk}-hotcold-legend`}>
                      <strong className="text-slate-800">How to read:</strong>
                      <span className="ml-1">Each row uses a different pool.</span>
                      <span className="ml-1"><strong className="text-red-600">H</strong> = hot (most 2026 hits at that pool),</span>
                      <span className="ml-1"><strong className="text-blue-600">C</strong> = cold, shown as <span className="font-mono">N &middot; gap&thinsp;d &middot; avg&thinsp;d</span>.</span>
                      <span className="ml-1"><strong>Rebound signal</strong>: if current gap &ge; avg, the number is overdue and historically bounces back.</span>
                    </div>
                    <table className="w-full text-xs">
                      <tbody>
                        {factors.map(f => {
                          const topGaps = f.cold?.top_gaps || [];
                          const coldNums = topGaps.length > 0 ? topGaps : (f.cold?.numbers || []).slice(0, 3).map(n => ({number: n}));
                          const hint = poolHint(f.key);
                          return (
                            <tr key={f.key} className="border-b border-gray-100 last:border-0">
                              <td className="px-4 py-1.5 w-40" title={hint}>
                                <span className="text-[10px] font-bold text-gray-500 uppercase leading-tight block">{f.label}</span>
                                {hint && <span className="text-[9px] text-gray-400 leading-tight block mt-0.5" data-testid={`pw-${bk}-hotcold-hint-${f.key}`}>{hint}</span>}
                              </td>
                              <td className="px-3 py-1.5">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-[9px] font-bold text-red-500 w-4">H</span>
                                  {(f.hot?.numbers || []).map(n => (
                                    <span key={n} className="inline-flex w-7 h-7 rounded-full bg-red-500 text-white items-center justify-center font-mono font-bold text-xs">{n}</span>
                                  ))}
                                  <span className="text-[10px] text-red-700 font-bold">{f.hot?.cy_count}x</span>
                                </div>
                              </td>
                              <td className="px-3 py-1.5">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-[9px] font-bold text-blue-500 w-4">C</span>
                                  {coldNums.map((g, i) => {
                                    const overdue = g.gap != null && g.avg_gap != null && g.gap >= g.avg_gap;
                                    return (
                                      <span key={i} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border ${overdue ? 'bg-rose-50 border-rose-300' : 'bg-blue-50 border-blue-200'}`}
                                            title={g.avg_gap != null ? `#${g.number || g} — current gap ${g.gap}d, avg gap ${g.avg_gap}d, max gap ${g.max_gap ?? '—'}d, ${g.hits_total ?? '—'} lifetime hits${overdue ? ' · OVERDUE' : ''}` : `#${g.number || g}`}>
                                        <span className={`inline-flex w-6 h-6 rounded-full text-white items-center justify-center font-mono font-bold text-[10px] ${overdue ? 'bg-rose-500' : 'bg-blue-500'}`}>{g.number || g}</span>
                                        {g.gap != null && <span className={`text-[9px] font-mono font-bold ${overdue ? 'text-rose-700' : 'text-blue-600'}`}>{g.gap}d</span>}
                                        {g.avg_gap != null && <span className="text-[8px] text-gray-500 font-mono">avg&nbsp;{g.avg_gap}d</span>}
                                      </span>
                                    );
                                  })}
                                  {f.cold?.cy_count === 0 && <span className="text-[9px] text-blue-400">0x in {new Date().getFullYear()}</span>}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="text-[9px] text-gray-400 text-right mt-1">{hc.total_cy_draws} draws in {new Date().getFullYear()}</div>
                  </div>
                );
              })()}
              {ranked.length > 0 && (
                <div className="border-t-2 border-[#002FA7] bg-gradient-to-r from-slate-50 to-blue-50 px-5 py-4" data-testid={`pw-${bk}-consensus`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Target size={18} weight="bold" className="text-[#002FA7]" />
                    <span className="text-sm font-bold text-gray-800">{label} — Most Agreed Numbers</span>
                    <span className="text-[10px] text-gray-500 ml-1">(appeared in N of {windows.length} windows)</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {ranked.slice(0, 15).map((r, ri) => (
                      <div key={r.number} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${r.count >= 7 ? 'bg-[#002FA7] text-white ring-2 ring-[#002FA7]/30' : r.count >= 5 ? 'bg-[#002FA7]/15 text-[#002FA7] ring-1 ring-[#002FA7]/20' : r.count >= 3 ? 'bg-blue-50 text-blue-800' : 'bg-gray-100 text-gray-600'}`} data-testid={`pw-${bk}-consensus-${r.number}`}>
                        <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-sm ${r.count >= 7 ? 'bg-white text-[#002FA7]' : r.count >= 5 ? 'bg-[#002FA7] text-white' : 'bg-white text-gray-800 border border-gray-200'}`}>
                          {r.number}
                        </span>
                        <div className="flex flex-col leading-tight">
                          <span className="font-bold text-xs">{r.count}/{windows.length}</span>
                          <span className="text-[9px] opacity-70">{r.totalScore.toFixed(1)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Cross-Ball Projection Total — Sum of window votes across all 5 positions */}
        {(() => {
          const crossBallTotals = {};
          const perBallBreakdown = {};
          Object.entries(ballLabels).forEach(([bk, label]) => {
            const windows = pwData.balls?.[bk] || [];
            const numFreq = {};
            windows.forEach(w => {
              w.predictions.forEach(p => {
                numFreq[p.number] = (numFreq[p.number] || 0) + 1;
              });
            });
            Object.entries(numFreq).forEach(([num, count]) => {
              const n = parseInt(num);
              crossBallTotals[n] = (crossBallTotals[n] || 0) + count;
              if (!perBallBreakdown[n]) perBallBreakdown[n] = {};
              perBallBreakdown[n][bk] = count;
            });
          });
          const sorted = Object.entries(crossBallTotals)
            .map(([num, total]) => ({ number: parseInt(num), total, breakdown: perBallBreakdown[parseInt(num)] || {} }))
            .sort((a, b) => b.total - a.total || a.number - b.number);
          if (sorted.length === 0) return null;
          return (
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-5 text-white" data-testid="cross-ball-projection-total">
              <div className="flex items-center gap-2 mb-4">
                <Target size={20} weight="bold" className="text-amber-400" />
                <h3 className="font-bold text-base">Top 25 Projection Across All Positions (B1-B5)</h3>
                <span className="text-[10px] text-white/50 ml-2">Sum of window prediction counts per number across all 5 ball positions</span>
                <button
                  onClick={() => {
                    const rows = sorted.map(r => ({
                      Number: r.number,
                      B1: r.breakdown.ball_1 || 0,
                      B2: r.breakdown.ball_2 || 0,
                      B3: r.breakdown.ball_3 || 0,
                      B4: r.breakdown.ball_4 || 0,
                      B5: r.breakdown.ball_5 || 0,
                      Positions: Object.values(r.breakdown).filter(v => v > 0).length,
                      Total: r.total,
                    }));
                    const ws = XLSX.utils.json_to_sheet(rows);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Total Projection B1-B5');
                    XLSX.writeFile(wb, `total_projection_B1-B5_${new Date().toISOString().slice(0, 10)}.xlsx`);
                  }}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-[10px] font-bold text-white/80 transition-colors"
                  data-testid="export-total-projection"
                >
                  <DownloadSimple size={14} weight="bold" />
                  Excel
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="px-3 py-2 text-left text-[10px] uppercase text-white/60 font-bold w-20">Number</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-white/60 font-bold">B1</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-white/60 font-bold">B2</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-white/60 font-bold">B3</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-white/60 font-bold">B4</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-white/60 font-bold">B5</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-white/60 font-bold">Positions</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-amber-400 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.slice(0, 25).map((r, idx) => {
                      const g = consensus.groupOf(r.number);
                      const gs = GROUP_STYLE[g];
                      return (
                      <tr key={r.number} className={`border-b border-white/5 ${idx < 5 ? 'bg-white/5' : ''}`} data-testid={`cross-total-${r.number}`}>
                        <td className="px-3 py-1.5">
                          <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-sm ring-2 ring-offset-1 ring-offset-slate-800 ${gs.ring} ${idx < 5 ? 'bg-amber-400 text-slate-900' : 'bg-white/15 text-white'}`} title={`${g} · Consensus ${consensus.scoreOf(r.number) >= 0 ? '+' : ''}${consensus.scoreOf(r.number)}`}>{r.number}</span>
                        </td>
                        {['ball_1','ball_2','ball_3','ball_4','ball_5'].map(bk => {
                          const v = r.breakdown[bk] || 0;
                          return (
                            <td key={bk} className="px-3 py-1.5 text-center">
                              <span className={`font-mono font-bold ${v >= 5 ? 'text-amber-400' : v > 0 ? 'text-white/80' : 'text-white/20'}`}>{v}x</span>
                            </td>
                          );
                        })}
                        <td className="px-3 py-1.5 text-center">
                          <span className={`font-mono font-bold ${Object.values(r.breakdown).filter(v => v > 0).length >= 4 ? 'text-amber-400' : 'text-white/70'}`}>
                            {Object.values(r.breakdown).filter(v => v > 0).length} Balls
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <span className={`font-mono font-bold text-sm ${r.total >= 15 ? 'text-amber-400' : r.total >= 10 ? 'text-white' : 'text-white/70'}`}>{r.total}x</span>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Actual Hits By Total — groups numbers by their consensus Total (window-vote count)
             and shows how many actual hits each Total-tier collected in the last 54 draws.
             Auto-updates on every new draw. */}
        <ActualHitsByTotalLeaderboard pwData={pwData} draws={54} />

        {/* Lucky Stars */}
        {Object.entries(lsLabels).map(([lk, label]) => {
          const windows = pwData.lucky_stars?.[lk] || [];
          if (windows.length === 0) return null;
          const freqMap = {};
          const scoreMap = {};
          windows.forEach(w => {
            w.predictions.forEach(p => {
              freqMap[p.number] = (freqMap[p.number] || 0) + 1;
              scoreMap[p.number] = (scoreMap[p.number] || 0) + p.score;
            });
          });
          const ranked = Object.entries(freqMap)
            .map(([num, count]) => ({ number: parseInt(num), count, totalScore: scoreMap[parseInt(num)] || 0 }))
            .sort((a, b) => b.count - a.count || b.totalScore - a.totalScore);
          return (
            <div key={lk} className="bg-white border border-yellow-300 rounded-lg overflow-hidden" data-testid={`pw-${lk}`}>
              <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-5 py-3 flex items-center gap-3">
                <Star size={22} weight="fill" className="text-white" />
                <h3 className="text-white font-bold text-lg">{label}</h3>
                <span className="text-white/60 text-xs ml-auto">{windows.length} windows</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-yellow-50">
                    <tr>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold w-14">S.No.</th>
                      <th className="px-4 py-2 text-left text-[10px] uppercase text-gray-600 font-bold w-48">Window</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">#1</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">#2</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">#3</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">#4</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">#5</th>
                    </tr>
                  </thead>
                  <tbody>
                    {windows.map((w, idx) => (
                      <tr key={`${w.window}-${w.name}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-yellow-50/30'} data-testid={`pw-${lk}-w${w.window}-${idx}`}>
                        <td className="px-3 py-2 border-b text-center font-mono font-bold text-gray-500" data-testid={`pw-${lk}-sno-${idx}`}>{idx + 1}</td>
                        <td className="px-4 py-2 border-b">
                          <button onClick={() => navigate(w.path)} className="flex items-center gap-2 hover:text-amber-700 transition-colors group">
                            <span className="inline-flex w-7 h-7 rounded bg-amber-100 items-center justify-center font-mono font-bold text-[10px] text-amber-800 group-hover:bg-amber-500 group-hover:text-white transition-colors">W{w.window}</span>
                            <span className="font-semibold text-gray-800 group-hover:text-amber-700">{w.name}</span>
                          </button>
                        </td>
                        {w.predictions.map((p, pi) => (
                          <td key={pi} className="px-3 py-2 border-b text-center">
                            <div className="inline-flex flex-col items-center">
                              <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-bold text-sm ${pi === 0 ? 'bg-amber-500 text-white' : pi < 3 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>
                                {p.number}
                              </span>
                              <span className="text-[9px] text-gray-400 mt-0.5">{p.score}</span>
                            </div>
                          </td>
                        ))}
                        {Array.from({length: Math.max(0, 5 - w.predictions.length)}).map((_, i) => (
                          <td key={`empty-${i}`} className="px-3 py-2 border-b text-center text-gray-300">-</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Average row for each column */}
              {windows.length > 0 && (() => {
                const avgs = [0,1,2,3,4].map(col => {
                  const nums = windows.map(w => w.predictions[col]?.number).filter(n => n != null);
                  return nums.length > 0 ? (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1) : null;
                });
                return (
                  <div className="border-t border-amber-200 bg-amber-50/60 px-5 py-2" data-testid={`pw-${lk}-average`}>
                    <table className="w-full text-xs">
                      <tbody>
                        <tr>
                          <td className="px-3 py-1.5 w-14 text-center text-[10px] font-bold text-amber-500 uppercase">—</td>
                          <td className="px-4 py-1.5 w-48 text-[10px] font-bold text-amber-700 uppercase">Avg</td>
                          {avgs.map((avg, i) => (
                            <td key={i} className="px-3 py-1.5 text-center">
                              {avg != null ? (
                                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-500 text-white font-mono font-bold text-sm">{avg}</span>
                              ) : <span className="text-gray-300">-</span>}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              {/* LS Number frequency across all windows */}
              {windows.length > 0 && (() => {
                const numFreq = {};
                const numWindows = {};
                windows.forEach(w => {
                  w.predictions.forEach(p => {
                    numFreq[p.number] = (numFreq[p.number] || 0) + 1;
                    if (!numWindows[p.number]) numWindows[p.number] = [];
                    numWindows[p.number].push(`W${w.window}`);
                  });
                });
                const sorted = Object.entries(numFreq)
                  .map(([num, count]) => ({ number: parseInt(num), count, windows: numWindows[parseInt(num)] }))
                  .sort((a, b) => b.count - a.count || a.number - b.number);
                return (
                  <div className="border-t border-amber-200 bg-amber-50/30 px-5 py-3" data-testid={`pw-${lk}-freq`}>
                    <div className="text-[10px] font-bold text-amber-700 uppercase mb-2">Prediction Frequency — Which number predicted how many times</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                      {sorted.map(r => (
                        <div key={r.number} className="flex items-center gap-1.5" data-testid={`pw-${lk}-freq-${r.number}`}>
                          <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-xs ${r.count >= 4 ? 'bg-amber-500 text-white' : r.count >= 2 ? 'bg-amber-200 text-amber-800 font-bold' : 'bg-gray-200 text-gray-700'}`}>
                            {r.number}
                          </span>
                          <span className={`text-xs font-mono ${r.count >= 4 ? 'text-amber-600 font-bold' : r.count >= 2 ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
                            {r.count}x
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* LS Hot/Cold: 3 factors */}
              {pwData.hot_cold?.[lk] && (() => {
                const hc = pwData.hot_cold[lk];
                const factors = [
                  { key: 'position', ...hc.position },
                  { key: 'all_ls', ...hc.all_ls },
                  { key: 'all7', ...hc.all7 },
                ];
                // Pool-scope hint per LS factor — explicitly calls out that
                // ALL 7 BALLS spans 1-50 (because it includes main balls),
                // while LUCKY STAR 1 uses 1-11 and LUCKY STAR 2 uses 2-12
                // (structural, because LS1 < LS2 always).
                const poolHint = (k) => {
                  if (k === 'position') return `Pool: ${hc.ls_position_range || '1-12'} at this LS position`;
                  if (k === 'all_ls')   return 'Pool: 1-12 across LS1+LS2';
                  if (k === 'all7')     return 'Pool: 1-50 across all 7 positions';
                  return '';
                };
                return (
                  <div className="border-t border-amber-200 bg-white px-5 py-2" data-testid={`pw-${lk}-hotcold`}>
                    {/* Legend */}
                    <div className="bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mb-2 text-[10px] text-amber-900 leading-snug" data-testid={`pw-${lk}-hotcold-legend`}>
                      <strong>How to read:</strong>
                      <span className="ml-1">Each row uses a different pool.</span>
                      <span className="ml-1"><strong className="text-red-600">H</strong> = hot (most 2026 hits at that pool),</span>
                      <span className="ml-1"><strong className="text-blue-600">C</strong> = cold, shown as <span className="font-mono">N &middot; gap&thinsp;d &middot; avg&thinsp;d</span>.</span>
                      <span className="ml-1"><strong>Rebound signal</strong>: if current gap &ge; avg gap, the number is overdue and historically bounces back.</span>
                    </div>
                    <table className="w-full text-xs">
                      <tbody>
                        {factors.map(f => {
                          const topGaps = f.cold?.top_gaps || [];
                          const coldNums = topGaps.length > 0 ? topGaps : (f.cold?.numbers || []).slice(0, 3).map(n => ({number: n}));
                          const hint = poolHint(f.key);
                          return (
                            <tr key={f.key} className="border-b border-gray-100 last:border-0">
                              <td className="px-4 py-1.5 w-40" title={hint}>
                                <span className="text-[10px] font-bold text-gray-500 uppercase leading-tight block">{f.label}</span>
                                {hint && <span className="text-[9px] text-gray-400 leading-tight block mt-0.5" data-testid={`pw-${lk}-hotcold-hint-${f.key}`}>{hint}</span>}
                              </td>
                              <td className="px-3 py-1.5">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-[9px] font-bold text-red-500 w-4">H</span>
                                  {(f.hot?.numbers || []).map(n => (
                                    <span key={n} className="inline-flex w-7 h-7 rounded-full bg-red-500 text-white items-center justify-center font-mono font-bold text-xs">{n}</span>
                                  ))}
                                  <span className="text-[10px] text-red-700 font-bold">{f.hot?.cy_count}x</span>
                                </div>
                              </td>
                              <td className="px-3 py-1.5">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-[9px] font-bold text-blue-500 w-4">C</span>
                                  {coldNums.map((g, i) => {
                                    const overdue = g.gap != null && g.avg_gap != null && g.gap >= g.avg_gap;
                                    return (
                                      <span key={i} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border ${overdue ? 'bg-rose-50 border-rose-300' : 'bg-blue-50 border-blue-200'}`}
                                            title={g.avg_gap != null ? `#${g.number || g} — current gap ${g.gap}d, avg gap ${g.avg_gap}d, max gap ${g.max_gap ?? '—'}d, ${g.hits_total ?? '—'} lifetime hits${overdue ? ' · OVERDUE' : ''}` : `#${g.number || g}`}>
                                        <span className={`inline-flex w-6 h-6 rounded-full text-white items-center justify-center font-mono font-bold text-[10px] ${overdue ? 'bg-rose-500' : 'bg-blue-500'}`}>{g.number || g}</span>
                                        {g.gap != null && <span className={`text-[9px] font-mono font-bold ${overdue ? 'text-rose-700' : 'text-blue-600'}`}>{g.gap}d</span>}
                                        {g.avg_gap != null && <span className="text-[8px] text-gray-500 font-mono">avg&nbsp;{g.avg_gap}d</span>}
                                      </span>
                                    );
                                  })}
                                  {f.cold?.cy_count === 0 && <span className="text-[9px] text-blue-400">0x in {new Date().getFullYear()}</span>}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="text-[9px] text-gray-400 text-right mt-1">{hc.total_cy_draws} draws in {new Date().getFullYear()}</div>
                  </div>
                );
              })()}
              {/* LS Consensus Summary */}
              {ranked.length > 0 && (
                <div className="border-t-2 border-amber-400 bg-gradient-to-r from-yellow-50 to-amber-50 px-5 py-4" data-testid={`pw-${lk}-consensus`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Star size={16} weight="fill" className="text-amber-500" />
                    <span className="text-sm font-bold text-gray-800">{label} — Most Agreed</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {ranked.map((r) => (
                      <div key={r.number} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${r.count >= windows.length ? 'bg-amber-500 text-white ring-2 ring-amber-300' : 'bg-amber-100 text-amber-900'}`}>
                        <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-sm ${r.count >= windows.length ? 'bg-white text-amber-600' : 'bg-white text-amber-800 border border-amber-200'}`}>
                          {r.number}
                        </span>
                        <div className="flex flex-col leading-tight">
                          <span className="font-bold text-xs">{r.count}/{windows.length}</span>
                          <span className="text-[9px] opacity-70">{r.totalScore.toFixed(1)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Lucky Stars Cross-Ball Projection — Numbers 1-12 across all 7 positions */}
        {(() => {
          const lsCrossTotals = {};
          const lsPerBallBreakdown = {};
          const allBallKeys = ['ball_1','ball_2','ball_3','ball_4','ball_5','lucky_star_1','lucky_star_2'];
          const allBallLabelsShort = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5', lucky_star_1: 'LS1', lucky_star_2: 'LS2' };

          // Count from main balls (B1-B5) — only numbers 1-12
          Object.keys(ballLabels).forEach(bk => {
            const windows = pwData.balls?.[bk] || [];
            windows.forEach(w => {
              w.predictions.forEach(p => {
                if (p.number >= 1 && p.number <= 12) {
                  lsCrossTotals[p.number] = (lsCrossTotals[p.number] || 0) + 1;
                  if (!lsPerBallBreakdown[p.number]) lsPerBallBreakdown[p.number] = {};
                  lsPerBallBreakdown[p.number][bk] = (lsPerBallBreakdown[p.number][bk] || 0) + 1;
                }
              });
            });
          });

          // Count from LS positions
          Object.keys(lsLabels).forEach(lk => {
            const windows = pwData.lucky_stars?.[lk] || [];
            windows.forEach(w => {
              w.predictions.forEach(p => {
                if (p.number >= 1 && p.number <= 12) {
                  lsCrossTotals[p.number] = (lsCrossTotals[p.number] || 0) + 1;
                  if (!lsPerBallBreakdown[p.number]) lsPerBallBreakdown[p.number] = {};
                  lsPerBallBreakdown[p.number][lk] = (lsPerBallBreakdown[p.number][lk] || 0) + 1;
                }
              });
            });
          });

          const sorted = Object.entries(lsCrossTotals)
            .map(([num, total]) => ({ number: parseInt(num), total, breakdown: lsPerBallBreakdown[parseInt(num)] || {} }))
            .sort((a, b) => b.total - a.total || a.number - b.number);

          if (sorted.length === 0) return null;
          return (
            <div className="bg-gradient-to-r from-amber-800 to-yellow-700 rounded-xl p-5 text-white" data-testid="ls-cross-ball-projection">
              <div className="flex items-center gap-2 mb-4">
                <Star size={20} weight="fill" className="text-yellow-300" />
                <h3 className="font-bold text-base">Lucky Stars — Total Projection Across All 7 Positions (Numbers 1-12)</h3>
                <span className="text-[10px] text-white/50 ml-2">Sum of window prediction counts for numbers 1-12 across B1-B5 + LS1 + LS2</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="px-3 py-2 text-left text-[10px] uppercase text-white/60 font-bold w-20">Number</th>
                      {allBallKeys.map(bk => (
                        <th key={bk} className={`px-3 py-2 text-center text-[10px] uppercase font-bold ${bk.startsWith('lucky') ? 'text-yellow-300' : 'text-white/60'}`}>{allBallLabelsShort[bk]}</th>
                      ))}
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-white/60 font-bold">Positions</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-yellow-300 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((r, idx) => (
                      <tr key={r.number} className={`border-b border-white/5 ${idx < 3 ? 'bg-white/5' : ''}`} data-testid={`ls-cross-${r.number}`}>
                        <td className="px-3 py-1.5">
                          <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-sm ${idx < 3 ? 'bg-yellow-400 text-yellow-900' : 'bg-white/15 text-white'}`}>{r.number}</span>
                        </td>
                        {allBallKeys.map(bk => {
                          const v = r.breakdown[bk] || 0;
                          return (
                            <td key={bk} className="px-3 py-1.5 text-center">
                              <span className={`font-mono font-bold ${v >= 5 ? 'text-yellow-300' : v > 0 ? 'text-white/80' : 'text-white/20'}`}>{v}x</span>
                            </td>
                          );
                        })}
                        <td className="px-3 py-1.5 text-center">
                          <span className={`font-mono font-bold ${Object.values(r.breakdown).filter(v => v > 0).length >= 4 ? 'text-yellow-300' : 'text-white/70'}`}>
                            {Object.values(r.breakdown).filter(v => v > 0).length} Balls
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <span className={`font-mono font-bold text-sm ${r.total >= 15 ? 'text-yellow-300' : r.total >= 10 ? 'text-white' : 'text-white/70'}`}>{r.total}x</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

      </div>
    ) : (
      <div className="text-center py-12 text-gray-500">Click the tab to load per-window predictions</div>
    )}
    </>
  );
}


/* ==========================================================================
 * ActualHitsByTotalLeaderboard
 * ------------------------------------------------------------------------------
 * Correlates the "Top 25 Projection Across All Positions (B1-B5)" table's
 * **Total** column (= how many window-position votes each number 1..50 received
 * across all 5 ball tabs) with **actual draw hits** over the last N draws.
 *
 * The user wants to answer:  "If consensus assigns Total = T to a group of
 * numbers, how often do those numbers actually get drawn?"
 *
 * For every number 1..50 we compute:
 *   total_i  = Σ (window-votes for i across B1..B5)   [from pwData]
 *   hits_i   = # of positional slots number i occupied across the last N draws
 *              (each draw has 5 ball positions, so N=54 → 270 slots).
 *
 * We then group numbers by total_i and show one row per Total tier, sorted
 * descending by Total (highest consensus first).  Each row exposes:
 *   Total | # of Numbers | Numbers list | Sum of Actual Hits | Avg Hits per Number | Group hit-rate
 * ========================================================================== */
function ActualHitsByTotalLeaderboard({ pwData, draws = 54 }) {
  const [drawList, setDrawList] = useState(null);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/draws?limit=${draws}`);
        if (cancelled) return;
        const list = r.data?.draws || [];
        setDrawList(list);
        if (list.length) {
          setDateRange({
            from: list[list.length - 1].draw_date,
            to:   list[0].draw_date,
            count: list.length,
          });
        }
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [draws]);

  // --- Build the number -> Total map from pwData (same logic as the parent's
  //     "Top 25 Projection Across All Positions" table). Total = sum of
  //     window-vote counts across all 5 ball positions.
  const totalByNumber = {};
  if (pwData) {
    ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'].forEach(bk => {
      const windows = pwData.balls?.[bk] || [];
      windows.forEach(w => {
        (w.predictions || []).forEach(p => {
          totalByNumber[p.number] = (totalByNumber[p.number] || 0) + 1;
        });
      });
    });
  }

  // --- Actual hits per number across all 5 ball positions in the last N draws.
  const hitsByNumber = {};
  if (drawList) {
    drawList.forEach(d => {
      ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'].forEach(bk => {
        const n = d[bk];
        if (n) hitsByNumber[n] = (hitsByNumber[n] || 0) + 1;
      });
    });
  }

  // --- Group by Total value.
  const groups = new Map(); // total -> { numbers:[], hitsSum, missingNumbers:[] }
  Object.entries(totalByNumber).forEach(([nStr, t]) => {
    const n = parseInt(nStr, 10);
    if (!groups.has(t)) groups.set(t, { total: t, numbers: [], hitsSum: 0, hitDetail: {} });
    const g = groups.get(t);
    g.numbers.push(n);
    const h = hitsByNumber[n] || 0;
    g.hitsSum += h;
    g.hitDetail[n] = h;
  });

  const totalSlots = drawList ? drawList.length * 5 : 0;

  // Sort control lives on the "Actual Hits (Σ)" column — user toggles asc/desc.
  // Default = desc (biggest actual hitters first).
  const [sortDir, setSortDir] = useState('desc');
  const rows = Array.from(groups.values()).sort((a, b) => {
    const cmp = a.hitsSum - b.hitsSum;
    if (cmp !== 0) return sortDir === 'asc' ? cmp : -cmp;
    // Tie-break: higher Total wins in desc mode, lower Total wins in asc mode.
    return sortDir === 'asc' ? (a.total - b.total) : (b.total - a.total);
  });

  // Cumulative % of all slots (running total, in whatever the current sort order is).
  const grandHits = rows.reduce((acc, r) => acc + r.hitsSum, 0);
  let running = 0;
  const rowsWithCum = rows.map(r => {
    const pct = totalSlots > 0 ? (r.hitsSum / totalSlots) * 100 : 0;
    running += pct;
    return { ...r, pctOfSlots: pct, cumPct: running };
  });

  const topRow = [...groups.values()].sort((a, b) => b.hitsSum - a.hitsSum || b.total - a.total)[0] || null;

  return (
    <div className="bg-gradient-to-br from-emerald-900 via-teal-900 to-emerald-900 rounded-xl p-5 text-white shadow-2xl border-2 border-emerald-600"
         data-testid="actual-hits-by-total-leaderboard">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <TrendUp size={22} weight="fill" className="text-emerald-300" />
        <div className="flex-1 min-w-0">
          <h3 className="font-black text-base uppercase tracking-wider">Which Totals hit the most? · last {draws} draws</h3>
          {dateRange && (
            <p className="text-[11px] text-emerald-200/80 mt-0.5 font-mono">
              {dateRange.from} → {dateRange.to} · {dateRange.count} draws ({totalSlots} positional slots) · auto-updates after every new draw
            </p>
          )}
        </div>
        {topRow && (
          <span className="px-2 py-1 rounded bg-amber-400 text-amber-900 text-[10px] font-black uppercase" data-testid="actual-hits-top-total">
            🏆 Most hits: Total {topRow.total} &middot; {topRow.hitsSum} of {totalSlots} slots ({totalSlots > 0 ? Math.round((topRow.hitsSum / totalSlots) * 1000) / 10 : 0}%)
          </span>
        )}
      </div>
      <p className="text-xs text-emerald-100/90 mb-3">
        Every number 1&ndash;50 receives a <strong>Total</strong> in the &ldquo;Top 25 Projection&rdquo; table above &mdash; the count of window-votes it collected across all 5 ball positions.
        This table groups numbers by their <strong>Total</strong> and shows what <strong>share of the last {draws} draws&apos; {totalSlots} positional slots</strong> each Total-tier owns.
        The <strong>% of All Slots</strong> column adds up to 100% &mdash; use the <strong>Cumulative %</strong> to spot where your picks should cluster (e.g. Totals 4&ndash;15 typically hold ~87% of all slots).
      </p>

      {error && <div className="text-rose-300 text-sm p-3 bg-rose-900/40 rounded" data-testid="actual-hits-error">Failed to load: {error}</div>}
      {!error && (drawList == null || !pwData) && <div className="text-emerald-200 text-sm" data-testid="actual-hits-loading">Loading last {draws} draws&hellip;</div>}
      {!error && drawList != null && pwData && rows.length === 0 && (
        <div className="text-emerald-200 text-sm italic">No projection totals available yet.</div>
      )}
      {!error && drawList != null && pwData && rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg bg-black/30">
          <table className="w-full text-xs" data-testid="actual-hits-table">
            <thead className="bg-emerald-950/70 text-emerald-200 uppercase text-[10px] font-black">
              <tr>
                <th className="px-3 py-2 text-center w-12">#</th>
                <th className="px-3 py-2 text-center">Total (consensus)</th>
                <th className="px-3 py-2 text-center"># of Numbers</th>
                <th className="px-3 py-2 text-left">Numbers in this Total group</th>
                <th className="px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))}
                    className="inline-flex items-center gap-1 hover:text-white transition"
                    data-testid="sort-hits-btn"
                    title={`Sort by Actual Hits — currently ${sortDir === 'desc' ? 'descending (highest first)' : 'ascending (lowest first)'}. Click to toggle.`}
                  >
                    Actual Hits (&Sigma;)
                    <span className="text-amber-300 text-[11px] font-black" data-testid="sort-hits-arrow">
                      {sortDir === 'desc' ? '▼' : '▲'}
                    </span>
                  </button>
                </th>
                <th className="px-3 py-2 text-center">% of All Slots ({totalSlots})</th>
                <th className="px-3 py-2 text-center">Cumulative %</th>
              </tr>
            </thead>
            <tbody>
              {rowsWithCum.map((r, i) => {
                const pctCls = r.pctOfSlots >= 10 ? 'text-emerald-300 font-black'
                              : r.pctOfSlots >= 5 ? 'text-emerald-200 font-bold'
                              : r.pctOfSlots >= 2 ? 'text-amber-200'
                              :                     'text-gray-400';
                const focusRange = r.total >= 4 && r.total <= 15;
                const sortedNums = [...r.numbers].sort((a, b) => (r.hitDetail[b] || 0) - (r.hitDetail[a] || 0) || a - b);
                return (
                  <tr
                    key={r.total}
                    className={`${i % 2 === 0 ? 'bg-white/5' : 'bg-white/0'} ${focusRange ? 'ring-1 ring-emerald-500/40' : ''}`}
                    data-testid={`actual-hit-total-row-${r.total}`}
                  >
                    <td className="px-3 py-1.5 text-center font-mono text-emerald-300">{i + 1}</td>
                    <td className="px-3 py-1.5 text-center">
                      <span
                        className={`inline-flex items-center justify-center w-12 h-9 rounded-lg font-mono font-black text-sm ${focusRange ? 'bg-emerald-400 text-emerald-950' : 'bg-amber-400 text-amber-900'}`}
                        data-testid={`actual-hit-total-${r.total}`}
                        title={focusRange ? 'In the 4–15 focus band' : ''}
                      >
                        {r.total}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-center font-mono text-emerald-100">{r.numbers.length}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {sortedNums.map(n => {
                          const h = r.hitDetail[n] || 0;
                          const badgeCls = h >= 3 ? 'bg-emerald-500 text-white'
                                          : h >= 1 ? 'bg-emerald-800 text-emerald-100'
                                          :          'bg-white/10 text-white/50';
                          return (
                            <span key={n} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[11px] ${badgeCls}`} title={`Number ${n} → ${h} actual hits`}>
                              <strong>{n}</strong>
                              <span className="opacity-80">·</span>
                              <span className="opacity-90">{h}h</span>
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className={`px-3 py-1.5 text-center font-mono text-lg ${pctCls}`} data-testid={`actual-hit-sum-${r.total}`}>{r.hitsSum}</td>
                    <td className={`px-3 py-1.5 text-center font-mono text-sm ${pctCls}`} data-testid={`actual-hit-pct-${r.total}`}>
                      {r.pctOfSlots.toFixed(1)}%
                    </td>
                    <td className="px-3 py-1.5 text-center font-mono text-emerald-200" data-testid={`actual-hit-cum-${r.total}`}>
                      {r.cumPct.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-emerald-500/60 bg-emerald-950/60">
                <td colSpan={4} className="px-3 py-2 text-right text-emerald-200 font-black uppercase text-[10px]">Focus band · Totals 4&ndash;15</td>
                {(() => {
                  const focus = rows.filter(r => r.total >= 4 && r.total <= 15);
                  const focusHits = focus.reduce((acc, r) => acc + r.hitsSum, 0);
                  const focusPct = totalSlots > 0 ? (focusHits / totalSlots) * 100 : 0;
                  return (
                    <>
                      <td className="px-3 py-2 text-center font-mono text-emerald-300 font-black" data-testid="focus-band-hits">{focusHits}</td>
                      <td className="px-3 py-2 text-center font-mono text-emerald-300 font-black" data-testid="focus-band-pct">{focusPct.toFixed(1)}%</td>
                      <td className="px-3 py-2 text-center font-mono text-emerald-200">of {totalSlots} slots</td>
                    </>
                  );
                })()}
              </tr>
              <tr className="bg-emerald-950/40">
                <td colSpan={4} className="px-3 py-2 text-right text-emerald-300/80 font-mono text-[10px] uppercase">Grand Total (all rows)</td>
                <td className="px-3 py-2 text-center font-mono text-emerald-300" data-testid="grand-total-hits">{grandHits}</td>
                <td className="px-3 py-2 text-center font-mono text-emerald-300">100.0%</td>
                <td className="px-3 py-2 text-center font-mono text-emerald-200">&mdash;</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!error && drawList != null && pwData && rows.length > 0 && (
        <p className="text-[11px] text-emerald-200/70 mt-3 italic">
          Definitions &mdash; <strong>Total</strong>: window-vote count each number received across all 5 ball positions (from the Top 25 Projection table above). <strong>Actual Hits (&Sigma;)</strong>: number of positional slots those numbers occupied across the last {draws} draws (each draw contributes 5 slots &rarr; {totalSlots} slots total). <strong>% of All Slots</strong> = Hits &divide; {totalSlots} &times; 100. <strong>Cumulative %</strong> = running total of the % column in the current sort order. Rows highlighted with the emerald ring are inside the <strong>4&ndash;15 focus band</strong> where picks should cluster.
        </p>
      )}
    </div>
  );
}
